import { Box } from "@material-ui/core";
import clsx from "clsx";
import { useDeviceTypes } from "halifax";
import React, { useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router";
import { Route } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  CfarOfferType,
  CoreFlightsCheckout,
  FlightBookType,
  FlightShopType,
  REVIEW_FLIGHT_DETAILS,
  REVIEW_MULTICITY_FLIGHT_DETAILS,
  TripCategory,
  VIEW_ITINERARY_RESTRICTIONS,
} from "redmond";

import { isCorpTenant } from "@capone/common";
import { config } from "../../api/config";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
  VOID_WINDOW_EXPERIMENT,
} from "../../context/experiments";
import { FLIGHT_CHECKOUT_TITLE, PORTAL_TITLE } from "../../lang/textConstants";
import {
  PATH_BOOK,
  PATH_BOOK_CONFIRMATION,
  PATH_BOOK_WITH_VI_CONFIRMATION,
} from "../../utils/urlPaths";
import { useChfarExerciseParams } from "../change-for-any-reason/hooks/useChfarExerciseParams";
import { useSyncChfarFlights } from "../change-for-any-reason/hooks/useSyncChfarFlights";
import {
  AgentBookingFeeModal,
  BookingErrorModal,
  BookingInProgressModal,
  BookingSuccessModal,
  PassportModal,
} from "./components";
import { FlightBookConnectorProps } from "./container";
import {
  setFetchAncillaryOfferResponse,
  setSelectedDisruptionProtectionId,
  setSelectedCfarId,
  setSelectedHtsCfarOfferId,
  setSelectedHtsDisruptionOfferId,
} from "../shop/actions/actions";
import "./styles.scss";
import { buildCoreFlightsCheckoutActions } from "./utils/mapCoreFlightsCheckout";

const { DesktopFlightBookWorkflow } = isCorpTenant(config.TENANT)
  ? require("./components/capone-corporate/DesktopFlightBookWorkflow")
  : require("./components/capone/DesktopFlightBookWorkflow");
const { MobileFlightBookWorkflow } = isCorpTenant(config.TENANT)
  ? require("./components/capone-corporate/MobileFlightBookWorkflow")
  : require("./components/capone/MobileFlightBookWorkflow");

export enum MobileFlightBookWorkflowStep {
  TravelerInformation,
  PricingAndPassengerValidation,
  AdditionalInformation,
  ContactInformation,
  SeatSelection,
  TravelOfferSelection,
  RewardsAndPayment,
  ApprovalReason,
  Review,
  BusinessLoyaltyProgram,
}
export interface IFlightBookProps
  extends RouteComponentProps,
    FlightBookConnectorProps {
  core?: CoreFlightsCheckout;
}

export const FlightBook = (props: IFlightBookProps) => {
  const {
    tripDetails,
    location,
    setUpFlightBookParams,
    history,
    finalizedItinerary,
    reviewFlightDetailsProperties,
    viewItineraryRestrictionsProperties,
    selectedAccount,
    isFlightBookWithAncillariesActive,
    priceQuote,
    resetFinalized,
    setQuote,
    closeSession,
    fetchTripSummariesV3,
    fetchMulticitySummaries,
    tripCategory,
    airportMap,
    shopSummarySearchString,
    flightBookType,
    core,
  } = props;
  const expState = useExperiments();
  const isVoidWindowExperiment = useMemo(
    () =>
      getExperimentVariant(expState.experiments, VOID_WINDOW_EXPERIMENT) ===
      AVAILABLE,
    [expState],
  );
  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const [eventSent, setEventSent] = useState(false);
  const [checkoutStep, setCheckoutStep] =
    useState<MobileFlightBookWorkflowStep>(0);
  const [localTravelerIds, setLocalTravelerIds] = useState<string[]>([]);
  const [localLapInfantIds, setLocalLapInfantIds] = useState<string[]>([]);

  const isMulticity = useMemo(
    () => tripCategory === TripCategory.MULTI_CITY,
    [tripCategory],
  );

  const dispatch = useDispatch();
  useEffect(() => {
    if (core != null) {
      buildCoreFlightsCheckoutActions(core).forEach(dispatch);
    }
  }, []);

  useEffect(() => {
    if (core != null) {
      dispatch(setSelectedDisruptionProtectionId(core.disruptionSelection?.id));
      dispatch(
        setSelectedHtsDisruptionOfferId(core.disruptionSelection?.id.productId),
      );
      dispatch(setSelectedHtsCfarOfferId(core.cfarSelection?.id.productId));
      if (core.cfarSelection) {
        dispatch(
          setSelectedCfarId({
            ...core.cfarSelection?.id,
            offerType: CfarOfferType.Hts,
          }),
        );
      }
      if (core.ancillaryOffersResponse) {
        dispatch(setFetchAncillaryOfferResponse(core.ancillaryOffersResponse));
      }
    }
  }, [core]);

  // For ChFAR exercise, we want to use the ChFAR shop instead of the regular shop.
  // The regular shop has a number of undesired side effects, such as price drop prediction, etc.
  const { flightShopType } = useChfarExerciseParams();
  const isChfarExercise =
    flightShopType === FlightShopType.CHANGE_FOR_ANY_REASON_EXERCISE;

  useSyncChfarFlights();

  useEffect(() => {
    document.title = FLIGHT_CHECKOUT_TITLE;
    return () => {
      // note: reset price quote states on destroy
      setQuote(null);
      resetFinalized();
      closeSession({});
      document.title = PORTAL_TITLE;
    };
  }, []);

  useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 0);
    if (!finalizedItinerary) {
      setUpFlightBookParams({ history });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (tripDetails && selectedAccount && !eventSent) {
      trackEvent({
        eventName: isMulticity
          ? REVIEW_MULTICITY_FLIGHT_DETAILS
          : REVIEW_FLIGHT_DETAILS,
        properties: { ...reviewFlightDetailsProperties },
      });
      trackEvent({
        eventName: VIEW_ITINERARY_RESTRICTIONS,
        properties: { ...viewItineraryRestrictionsProperties },
      });
      setEventSent(true);
    }
  }, [tripDetails, selectedAccount]);

  useEffect(() => {
    if (
      isVoidWindowExperiment &&
      flightBookType === FlightBookType.DEFAULT &&
      !airportMap &&
      !!shopSummarySearchString &&
      !isChfarExercise
    ) {
      // imitate shop search params so the saga has access to the right values
      if (isMulticity) {
        fetchMulticitySummaries({
          ...history,
          location: {
            ...history.location,
            search: `?${shopSummarySearchString}`,
          },
        });
      } else {
        fetchTripSummariesV3({
          ...history,
          location: {
            ...history.location,
            search: `?${shopSummarySearchString}`,
          },
        });
      }
    }
  }, [
    isVoidWindowExperiment,
    flightBookType,
    history,
    shopSummarySearchString,
  ]);

  return (
    <Box
      className={clsx(
        "flight-book-root",
        {
          confirm: [
            PATH_BOOK_CONFIRMATION,
            PATH_BOOK_WITH_VI_CONFIRMATION,
          ].includes(location.pathname),
        },
        { mobile: matchesMobile },
      )}
    >
      <Route path={PATH_BOOK} exact>
        {tripDetails && (
          <>
            <Box className="flight-book-container">
              {matchesDesktop && <DesktopFlightBookWorkflow />}
              {matchesMobile && (
                <MobileFlightBookWorkflow
                  checkoutStep={checkoutStep}
                  setCheckoutStep={setCheckoutStep}
                  localLapInfantIds={localLapInfantIds}
                  setLocalLapInfantIds={setLocalLapInfantIds}
                  localTravelerIds={localTravelerIds}
                  setLocalTravelerIds={setLocalTravelerIds}
                />
              )}
            </Box>
            <PassportModal
              onCloseMobileModal={() =>
                setCheckoutStep(
                  MobileFlightBookWorkflowStep.TravelerInformation,
                )
              }
            />
          </>
        )}
        <BookingErrorModal
          checkoutStep={checkoutStep}
          setCheckoutStep={setCheckoutStep}
          setLocalLapInfantIds={setLocalLapInfantIds}
          setLocalTravelerIds={setLocalTravelerIds}
        />
        <BookingInProgressModal
          // note: hide price-quote-in-progress modal in the flight-book variant of https://app.launchdarkly.com/capital-one/test/features/c1-fintech-ancillary-marketplace/targeting
          hidePriceQuoteInProgress={
            isFlightBookWithAncillariesActive && matchesDesktop && !!priceQuote
          }
        />
      </Route>
      <Route path={[PATH_BOOK_CONFIRMATION, PATH_BOOK_WITH_VI_CONFIRMATION]}>
        <BookingSuccessModal isMultiCity={isMulticity} />
        <AgentBookingFeeModal />
      </Route>
    </Box>
  );
};
